import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import evacuationService from '../services/evacuation/evacuation-service.js';
import screenshotService from '../services/evacuation/screenshot-service.js';
import Swal from 'sweetalert2';

// ==================== QUERIES (Read Operations) ====================
export const useEvacuationEvents = (userId) => {
  return useQuery({
    queryKey: ['evacuation', 'events', userId],
    queryFn: () => evacuationService.getEvents(userId),
    enabled: !!userId,
    staleTime: 1000 * 60 * 2,
  });
};

export const useActiveEvent = (userId) => {
  return useQuery({
    queryKey: ['evacuation', 'events', 'active', userId],
    queryFn: () => evacuationService.getActiveEvent(userId),
    enabled: !!userId,
    staleTime: 1000 * 60 * 2,
  });
};

export const useEvacuationEvent = (eventId) => {
  return useQuery({
    queryKey: ['evacuation', 'events', eventId],
    queryFn: () => evacuationService.getEvent(eventId),
    enabled: !!eventId,
  });
};

export const useEvacuationLayers = (isAuthenticated = true, options = {}) => {
  return useQuery({
    queryKey: ['evacuation', 'layers'],
    queryFn: () => evacuationService.getLayers(),
    enabled: isAuthenticated, // Only run when user is authenticated
    staleTime: 1000 * 60 * 5, // 5 minutes - layers can change per client context
    gcTime: 1000 * 60 * 60, // Keep in cache for 1 hour
    refetchOnMount: true, // Refetch if stale (happens after client switch invalidation)
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    ...options,
  });
};

export const useZoneSettings = (eventId) => {
  return useQuery({
    queryKey: ['evacuation', 'zones', 'settings', eventId],
    queryFn: () => evacuationService.getZoneSettings(eventId),
    enabled: !!eventId,
    staleTime: 1000 * 60 * 5,
  });
};

export const useEvacuationZonesForEvent = (eventId) => {
  return useQuery({
    queryKey: ['evacuation', 'zones', 'event', eventId],
    queryFn: () => evacuationService.getZonesForEvent(eventId),
    enabled: !!eventId,
    staleTime: Infinity,
    gcTime: 1000 * 60 * 60,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};

export const useEvacuationOrders = (userId, filters = {}, eventsLoaded = true) => {
  return useQuery({
    queryKey: ['evacuation', 'orders', userId, filters],
    queryFn: () => evacuationService.getOrders(userId, filters),
    enabled: !!userId && eventsLoaded, // Only run when userId exists AND events are loaded
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
};

// ==================== MUTATIONS (Write Operations) ====================
export const useCreateEvent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (eventData) => evacuationService.createEvent(eventData),

    onSuccess: (data, variables) => {
      // Invalidate all event queries to refetch fresh data
      queryClient.invalidateQueries(['evacuation', 'events']);

      Swal.fire({
        icon: 'success',
        title: 'Event Created',
        text: `Evacuation event has been created successfully.`,
        timer: 2000,
        showConfirmButton: false,
      });
    },

    onError: (error) => {
      const errorMessage = error.response?.data?.message || error.message || 'Could not create evacuation event.';

      Swal.fire({
        icon: 'error',
        title: 'Failed to Create Event',
        text: errorMessage,
      });
    },
  });
};

export const useEndEvent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ eventId, endData }) => evacuationService.endEvent(eventId, endData),

    onSuccess: (data, variables) => {
      // Invalidate related queries
      // Option 1: Invalidate everything (current approach - works but broad)
      queryClient.invalidateQueries(['evacuation', 'events']);

      // Option 2: Precise invalidation (recommended if userId available)
      // If you pass userId in variables:
      // queryClient.invalidateQueries(['evacuation', 'events', variables.userId]);
      // queryClient.invalidateQueries(['evacuation', 'events', 'active', variables.userId]);
      // queryClient.invalidateQueries(['evacuation', 'events', variables.eventId]);

      Swal.fire({
        icon: 'success',
        title: 'Event Ended',
        text: 'The evacuation event has been ended.',
        timer: 2000,
        showConfirmButton: false,
      });
    },

    onError: (error) => {
      Swal.fire({
        icon: 'error',
        title: 'Failed to End Event',
        text: error.message,
      });
    },
  });
};

export const useCreateEvacuationOrder = () => {
  /**
   * Create/calculate evacuation order mutation
   * 
   * This mutation is used for:
   * 1. Initial order calculation (when zone is selected)
   * 2. Recalculation (when parameters change: percentage, TCP, lanes)
   * 3. Fetching existing order data (for committed/rescinded orders)
   * 
   * The mutation returns enhanced zone data including:
   * - addresses: Array of address data in zone
   * - order_details: Existing order info (if committed)
   * - calculations: Fresh calculation results
   * - evacuation_data: Zone and destination info
   * - order_geojson: Route GeoJSON for map display
   * 
   * @returns {UseMutationResult}
   */

  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      userId,
      eventId,
      zoneId,
      hasBaseParameters = false,
      percentageRemaining = 100,
      tcpEnabled = false,
      effectiveLanes = 1.0,
    }) => {
      if (!zoneId) {
        throw new Error('Zone ID is required');
      }

      if (!eventId) {
        throw new Error('Event ID is required');
      }

      const orderData = await evacuationService.createOrder(
        userId,
        eventId,
        zoneId,
        hasBaseParameters,
        percentageRemaining,
        tcpEnabled,
        effectiveLanes,
      );

      return orderData;
    },

    onSuccess: (data, variables) => {
      // Log success for debugging
      console.log(`Order calculation completed for zone ${variables.zoneId}`);
    },

    onError: (error, variables) => {
      console.error('Error creating/calculating evacuation order:', error);

      // Determine error message
      let errorMessage = 'Could not calculate evacuation order.';

      if (error.message?.includes('Zone ID')) {
        errorMessage = 'Invalid zone selected.';
      } else if (error.message?.includes('Event ID')) {
        errorMessage = 'No active event found.';
      } else if (error.response?.status === 404) {
        errorMessage = 'Zone or destination data not found.';
      } else if (error.response?.status === 500) {
        errorMessage = 'Server error. Please try again.';
      } else if (error.message) {
        errorMessage = error.message;
      }

      Swal.fire({
        icon: 'error',
        title: 'Calculation Failed',
        text: errorMessage,
        footer: `Zone ID: ${variables.zoneId}`,
      });
    },

    retry: 1, // Retry once on failure
    retryDelay: 1000, // Wait 1 second before retry
  });
};

export const useCommitEvacuationOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (commitData) => {
      // Validate required fields
      if (!commitData.orderId) {
        throw new Error('Order ID is required');
      }

      if (!commitData.firstName || !commitData.lastName) {
        throw new Error('First name and last name are required');
      }

      if (!commitData.commitOrderDate || !commitData.commitOrderTime) {
        throw new Error('Order date and time are required');
      }

      // Call service with all data
      return await evacuationService.commitOrder(commitData);
    },

    onSuccess: (data, variables) => {
      // Refetch zone statuses (even while the portal map is unmounted) so colors update on next visit
      queryClient.invalidateQueries({ queryKey: ['evacuation', 'zones', 'event'], refetchType: 'all' });
      console.log('✅ Order committed successfully:', data);
    },

    onError: (error) => {
      console.error('❌ Error committing evacuation order:', error);

      Swal.fire({
        icon: 'error',
        title: 'Failed to Commit Order',
        text: error.message || 'Could not commit the evacuation order.',
      });
    },
  });
};

export const useAlertEvacuationOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (alertData) => {
      if (!alertData.orderId) {
        throw new Error('Order ID is required');
      }

      if (!alertData.firstName || !alertData.lastName) {
        throw new Error('First name and last name are required');
      }

      return await evacuationService.alertOrder(alertData);
    },

    onSuccess: (data) => {
      // Refetch zone statuses (even while the portal map is unmounted) so colors update on next visit
      queryClient.invalidateQueries({ queryKey: ['evacuation', 'zones', 'event'], refetchType: 'all' });
      console.log('✅ Order alerted successfully:', data);
    },

    onError: (error) => {
      console.error('❌ Error alerting evacuation order:', error);

      Swal.fire({
        icon: 'error',
        title: 'Failed to Alert Order',
        text: error.message || 'Could not alert the evacuation order.',
      });
    },
  });
};

export const useRescindEvacuationOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (rescindData) => {
      // Validate required fields
      if (!rescindData.orderId) {
        throw new Error('Order ID is required');
      }

      if (!rescindData.firstName || !rescindData.lastName) {
        throw new Error('First name and last name are required');
      }

      if (!rescindData.rescindOrderDate || !rescindData.rescindOrderTime) {
        throw new Error('Rescind date and time are required');
      }

      // Call service with all data
      return await evacuationService.rescindOrder(rescindData);
    },

    onSuccess: (data, variables) => {
      // Refetch zone statuses (even while the portal map is unmounted) so colors update on next visit
      queryClient.invalidateQueries({ queryKey: ['evacuation', 'zones', 'event'], refetchType: 'all' });
      console.log('✅ Order rescinded successfully:', data);
    },

    onError: (error) => {
      console.error('❌ Error rescinding evacuation order:', error);

      Swal.fire({
        icon: 'error',
        title: 'Failed to Rescind Order',
        text: error.message || 'Could not rescind the evacuation order.',
      });
    },
  });
};

export const useGenerateScreenshot = () => {
  return useMutation({
    mutationFn: async ({ url, authToken, sectionsToHide }) => {
      // Call service to generate screenshot
      const blob = await screenshotService.generateScreenshot({
        url,
        authToken,
        sectionsToHide,
      });

      return blob;
    },

    onSuccess: (blob) => {
      console.log('✅ Screenshot generated successfully:', {
        size: blob.size,
        type: blob.type,
      });
    },

    onError: (error) => {
      console.error('❌ Error generating screenshot:', error);

      Swal.fire({
        icon: 'error',
        title: 'Screenshot Failed',
        text: error.message || 'Could not generate screenshot.',
      });
    },

    // Screenshot generation can be slow, don't retry
    retry: false,
  });
};